/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { media, UNSAFE_media } from '@atlaskit/primitives/responsive';
import Stack from '@atlaskit/primitives/stack';
import { N0, N30, N40, N800 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';

import { ColorTable, GroupedColorTable } from '../types';

const tableStyles = css({
  [UNSAFE_media.below.sm]: {
    display: 'block',
  },
});

const tableHeadStyles = css({
  [UNSAFE_media.below.sm]: {
    display: 'none',
  },
});

const tableBodyStyles = css({
  [UNSAFE_media.below.sm]: {
    display: 'block',
  },
});

const rowStyles = css({
  '&::not(::last-of-type)': {
    borderBottom: `1px solid ${token('color.border', N30)}`,
  },
  '&::last-of-type': {
    borderBottom: 0,
  },

  [UNSAFE_media.below.sm]: {
    display: 'grid',
    paddingTop: token('space.150', '12px'),
    paddingBottom: token('space.150', '12px'),
    rowGap: token('space.100', '8px'),
  },
});

const cellStyles = css({
  [UNSAFE_media.below.sm]: {
    display: 'block',
    padding: token('space.0', '0px'),
  },

  [media.above.sm]: {
    paddingTop: token('space.200', '16px'),
    paddingBottom: token('space.200', '16px'),
  },
});

const headerRowStyles = css({
  [UNSAFE_media.below.sm]: {
    display: 'none',
  },
});

const tagContainerStyles = css({
  display: 'flex',
  width: 'fit-content',
  height: 'fit-content',
  padding: `${token('space.050', '4px')} ${token('space.100', '8px')}`,
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'row',
  borderRadius: '100px',
});

const colorDotStyles = css({
  width: '0.75rem',
  height: '0.75rem',
  borderRadius: '50%',

  [media.above.sm]: {
    width: '1rem',
    height: '1rem',
  },
});

const tokenNameStyles = css({
  marginRight: token('space.100', '8px'),
  fontSize: token('font.size.075', '12px'),
  lineHeight: token('font.lineHeight.100', '16px'),

  [media.above.sm]: {
    fontSize: token('font.size.100', '14px'),
  },
});

const renderTag = ({
  name,
  background,
  input,
  inverse,
  icon,
  token: iconToken,
}: ColorTable) => (
  <div
    css={tagContainerStyles}
    style={{
      backgroundColor:
        background ||
        token('color.background.neutral', 'rgba(9, 30, 66, 0.06)'),
      border: `1px solid ${input ? token('color.border.input', N40) : 'none'}`,
    }}
  >
    <code
      css={tokenNameStyles}
      style={{
        color: inverse
          ? token('color.text.inverse', N0)
          : token('color.text', N800),
      }}
    >
      {name}
    </code>
    {icon ? (
      <div css={colorDotStyles}>{icon}</div>
    ) : (
      <div
        css={colorDotStyles}
        style={{
          backgroundColor: iconToken,
        }}
      />
    )}
  </div>
);

/**
 * __Base Table__
 *
 * A base color table that formed by color names the left, and descriptions on the right.
 *
 */
const BaseTable = ({
  tableData,
  categoryName,
}: {
  tableData: ColorTable[] | GroupedColorTable;
  categoryName: string;
}) => {
  return (
    <table css={tableStyles}>
      <thead css={tableHeadStyles}>
        <tr css={[rowStyles, headerRowStyles]}>
          <th css={cellStyles}>{categoryName}</th>
          <th css={cellStyles}>Description</th>
        </tr>
      </thead>
      <tbody css={tableBodyStyles}>
        {Array.isArray(tableData)
          ? tableData.map((item: ColorTable) => (
              <tr key={item.name} css={rowStyles}>
                <td css={cellStyles}>{renderTag(item)}</td>
                <td css={cellStyles} style={{ whiteSpace: 'pre-line' }}>
                  {item.description}
                </td>
              </tr>
            ))
          : Object.values(tableData).map((groupedItems: ColorTable[]) => (
              <tr key={groupedItems[0].name} css={rowStyles}>
                <td css={cellStyles}>
                  <Stack space="space.100">
                    {groupedItems.map((item: ColorTable) => renderTag(item))}
                  </Stack>
                </td>
                <td css={cellStyles} style={{ whiteSpace: 'pre-line' }}>
                  {groupedItems[0].description}
                </td>
              </tr>
            ))}
      </tbody>
    </table>
  );
};

export default BaseTable;
